// =====================================================================
// /admin/overtime - Heures Supplémentaires (Faza 3.5 Round 1)
// =====================================================================
// Layout:
//   - Titlu "Heures Supplémentaires"
//   - 2 tab-uri: Ajouter (funcțional Round 1) | Soldes (placeholder Round 2)
//
// Tab Ajouter:
//   - Form 4 fields per Image 1 Odoo: OPÉRATEUR, HEURES (+/-), DATE, NOTE
//   - Submit POST /api/overtime cu cap check si warning detection
//   - Feedback inline pentru succes/eroare
//
// Permission needed: overtime.create.all (a.k.a. encode hours)
// Backend: overtime.addEntry (api.js)
// =====================================================================

import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { overtime as overtimeApi, users as usersApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { comparerPersonnes, marqueurInactif } from '../../lib/tri-personnes.js';
import { celluleCsv } from '../../lib/csv.js';

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Parse hh:mm (with optional sign) or decimal fallback. Returns hours as Number, NaN if invalid.
// Accepted: "1:30", "+1:30", "-0:45", "1.5", "+2", "-0.5"
function parseHHMM(input) {
  if (typeof input !== 'string') return NaN;
  const s = input.trim();
  if (!s) return NaN;
  const m = s.match(/^([+-]?)(\d+):([0-5]?\d)$/);
  if (m) {
    const sign = m[1] === '-' ? -1 : 1;
    const h = parseInt(m[2], 10);
    const mm = parseInt(m[3], 10);
    return sign * (h + mm / 60);
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : NaN;
}

// Format decimal hours as signed hh:mm. Ex: 1.5 -> "+1:30", -0.75 -> "-0:45", 0 -> "0:00"
function formatBalanceHours(h) {
  if (h === null || h === undefined || !Number.isFinite(h) || h === 0) return '0:00';
  const sign = h > 0 ? '+' : '-';
  const totalMin = Math.round(Math.abs(h) * 60);
  const H = Math.floor(totalMin / 60);
  const M = totalMin % 60;
  return `${sign}${H}:${String(M).padStart(2, '0')}`;
}

export default function AdminOvertime({ embedded = false }) {
  const { can } = useAuth();
  const location = useLocation();
  // Day 9: support nested hash like #overtime:soldes-operateurs (second segment selects sub-tab)
  const OVERTIME_SUB_TABS = ['ajouter', 'soldes', 'soldes-operateurs'];
  const subHash = (location.hash.replace('#', '').split(':')[1] || '').trim();
  const initialSub = OVERTIME_SUB_TABS.includes(subHash) ? subHash : 'ajouter';
  const [activeTab, setActiveTab] = useState(initialSub);

  useEffect(() => {
    if (OVERTIME_SUB_TABS.includes(subHash)) setActiveTab(subHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const canEncodeAll      = can('overtime.create.all');
  const canEncodeDelegate = can('overtime.create.delegate'); // Day 14: encode scopé (ex Tani → Pierre & Austin)
  const canEncode  = canEncodeAll || canEncodeDelegate;
  const canReadAll = can('overtime.read.all');
  const canReadOwn = can('overtime.read.own');  // Day 9: operators see their own row in Soldes opérateurs
  // 02-10-2026 (Silviu) — le compteur est chargé par le PARENT, pas par l'onglet :
  // un badge qu'il faut ouvrir l'onglet pour voir ne sert à rien. C'est ainsi que
  // 25 décisions de septembre sont restées invisibles — l'onglet s'appelait
  // « Soldes », et personne n'allait y chercher des décisions.
  const [nbEnAttente, setNbEnAttente] = useState(null);
  useEffect(() => {
    let vivant = true;
    const charger = canReadAll || can('overtime.decide.all')
      ? () => overtimeApi.adminAllPendingDecisions('PENDING')
      : () => overtimeApi.myPendingDecisions('PENDING');
    charger()
      .then((l) => { if (vivant) setNbEnAttente(Array.isArray(l) ? l.length : 0); })
      .catch(() => { if (vivant) setNbEnAttente(null); });   // jamais bloquant : pas de badge, pas d'erreur
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Export state (M-006 Day 5) ----
  const todayMonthStart = todayYmd().slice(0, 8) + '01'; // YYYY-MM-01
  const [exportFrom,   setExportFrom]   = useState(todayMonthStart);
  const [exportTo,     setExportTo]     = useState(todayYmd());
  const [exportUserId, setExportUserId] = useState(''); // '' = tous
  const [exportType,   setExportType]   = useState(''); // '' = tous
  const [exporting,    setExporting]    = useState(null); // null | 'csv' | 'xlsx'
  const [exportError,  setExportError]  = useState(null);
  const [exportUsers,  setExportUsers]  = useState([]);

  // Load users list for filter dropdown (admin only)
  useEffect(() => {
    if (!canReadAll) return;
    // Comptes INACTIFS compris : la paie exporte aussi les heures d'un
    // opérateur parti en cours d'année. Marqués « (inactif) », pas cachés.
    // (L'appel portait `isActive: true`, une clé inconnue du serveur, jetée
    // en silence : la liste était déjà complète.)
    usersApi.list()
      .then(data => {
        const list = Array.isArray(data) ? data : (data.items || data.users || []);
        setExportUsers(list);
      })
      .catch(err => console.error('[AdminOvertime] users load error', err));
  }, [canReadAll]);

  const handleExport = async (format) => {
    if (exporting) return;
    setExporting(format);
    setExportError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('format', format);
      if (exportFrom)   qs.set('from',   exportFrom);
      if (exportTo)     qs.set('to',     exportTo);
      if (exportUserId) qs.set('userId', exportUserId);
      if (exportType)   qs.set('type',   exportType);

      const res = await fetch(`/api/overtime/export?${qs.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const body = await res.json(); msg = body.message || body.error || msg; } catch (_) {}
        throw new Error(msg);
      }

      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `overtime_${todayYmd()}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[AdminOvertime] export error', err);
      setExportError(`Erreur export ${format.toUpperCase()}: ${err.message || err}`);
    } finally {
      setExporting(null);
    }
  };

  const exportBtnStyle = (format, loading) => ({
    background: format === 'csv' ? '#3b82f6' : '#16a34a',
    color: '#fff',
    border: 0,
    padding: '8px 14px',
    borderRadius: 6,
    fontWeight: 500,
    fontSize: 13,
    cursor: loading ? 'wait' : 'pointer',
    opacity: loading ? 0.6 : 1,
  });

  return (
    <div style={{ padding: embedded ? 0 : 24, maxWidth: 1200 }}>
      {!embedded && <h1 style={{ marginBottom: 16, fontSize: 24 }}>Heures Supplémentaires</h1>}

      {/* Export bar (M-006 Day 5) - admin only */}
      {canReadAll && (
        <div style={{
          background: '#F8FAFC',
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Export
          </span>
          <label style={{ fontSize: 12, color: '#64748b' }}>Du{' '}
            <input
              type="date"
              value={exportFrom}
              onChange={e => setExportFrom(e.target.value)}
              style={{ padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 4, fontSize: 13 }}
            />
          </label>
          <label style={{ fontSize: 12, color: '#64748b' }}>Au{' '}
            <input
              type="date"
              value={exportTo}
              onChange={e => setExportTo(e.target.value)}
              style={{ padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 4, fontSize: 13 }}
            />
          </label>
          <select
            value={exportUserId}
            onChange={e => setExportUserId(e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 4, fontSize: 13 }}
          >
            <option value="">Tous opérateurs</option>
            {exportUsers.map(u => (
              <option key={u.id} value={u.id}>{u.name || u.username || u.id}{marqueurInactif(u)}</option>
            ))}
          </select>
          <select
            value={exportType}
            onChange={e => setExportType(e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid #CBD5E1', borderRadius: 4, fontSize: 13 }}
          >
            <option value="">Tous types</option>
            <option value="MANUAL">Manuel</option>
            <option value="AUTO_FROM_TS">Auto (timesheet)</option>
            <option value="REC_CONSUME">Récupération</option>
            <option value="PAID_OUT">Payé</option>
            <option value="DEDUCTED_SALARY">Déduit salaire</option>
            <option value="CARRY_FORWARD">Reporté</option>
          </select>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              onClick={() => handleExport('csv')}
              disabled={exporting !== null}
              style={exportBtnStyle('csv', exporting === 'csv')}
              title="Exporter en CSV"
            >
              {exporting === 'csv' ? '...' : 'CSV'}
            </button>
            <button
              onClick={() => handleExport('xlsx')}
              disabled={exporting !== null}
              style={exportBtnStyle('xlsx', exporting === 'xlsx')}
              title="Exporter en XLSX"
            >
              {exporting === 'xlsx' ? '...' : 'XLSX'}
            </button>
          </div>

          {exportError && (
            <div style={{ flexBasis: '100%', marginTop: 8, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 4, fontSize: 12 }}>
              {exportError}
            </div>
          )}
        </div>
      )}

      {/* Tab navigation */}
      <div style={{
        display: 'flex',
        gap: 4,
        borderBottom: '2px solid #e2e8f0',
        marginBottom: 24,
      }}>
        <TabButton
          active={activeTab === 'ajouter'}
          onClick={() => setActiveTab('ajouter')}
          disabled={!canEncode}
          title={!canEncode ? "Vous n'avez pas la permission d'encoder des heures (overtime.create.all ou .delegate)" : ''}
        >
          Ajouter
        </TabButton>
        <TabButton
          active={activeTab === 'soldes'}
          onClick={() => setActiveTab('soldes')}
        >
          Décisions
          {nbEnAttente > 0 && (
            <span style={{
              marginLeft: 6, padding: '1px 7px', borderRadius: 999,
              background: '#b3261e', color: '#fff', fontSize: 11, fontWeight: 700,
            }}>{nbEnAttente}</span>
          )}
        </TabButton>
        <TabButton
          active={activeTab === 'soldes-operateurs'}
          onClick={() => setActiveTab('soldes-operateurs')}
          disabled={!canReadAll && !canReadOwn}
          title={(!canReadAll && !canReadOwn) ? "Pas de permission overtime.read.own" : ''}
        >
          {canReadAll ? 'Soldes opérateurs' : 'Mon solde'}
        </TabButton>
      </div>

      {activeTab === 'ajouter' && (
        canEncode
          ? <AjouterTab />
          : <div style={{
              padding: 24, background: '#FEF3C7', color: '#78350F',
              borderRadius: 6, border: '1px solid #F59E0B',
            }}>
              <strong>Accès refusé.</strong> Vous n'avez pas la permission d'encoder des heures
              {' '}(<code>overtime.create.all</code> ou <code>overtime.create.delegate</code>). Contactez Silviu pour autorisation.
            </div>
      )}

      {activeTab === 'soldes' && <SoldesTab onCount={setNbEnAttente} />}

      {activeTab === 'soldes-operateurs' && (
        (canReadAll || canReadOwn)
          ? <SoldesOperateursTab canEdit={canEncode} selfOnly={!canReadAll} />
          : <div style={{
              padding: 24, background: '#FEF3C7', color: '#78350F',
              borderRadius: 6, border: '1px solid #F59E0B',
            }}>
              <strong>Accès refusé.</strong>
            </div>
      )}
    </div>
  );
}

// =====================================================================
// Sub-component: TabButton
// =====================================================================
function TabButton({ active, onClick, disabled, title, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: '10px 24px',
        background: active ? 'white' : 'transparent',
        color: disabled ? '#94a3b8' : (active ? '#1e40af' : '#475569'),
        border: 'none',
        borderBottom: active ? '2px solid #1e40af' : '2px solid transparent',
        marginBottom: -2,
        fontSize: 14,
        fontWeight: active ? 600 : 400,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {children}
    </button>
  );
}

// =====================================================================
// Sub-component: AjouterTab (form Ajouter overtime)
// =====================================================================
function AjouterTab() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Form state
  const [userId, setUserId] = useState('');
  const [hours, setHours] = useState('');
  const [date, setDate] = useState(todayYmd());
  const [note, setNote] = useState('');

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error' | 'warning', message }

  useEffect(() => {
    // Day 14: liste scopée côté serveur (tous les actifs si create.all ; délégués si create.delegate).
    overtimeApi.encodableUsers()
      .then(data => {
        const list = Array.isArray(data) ? data : (data.items || data.users || []);
        setUsers(list);
        setLoadingUsers(false);
      })
      .catch(err => {
        setFeedback({ type: 'error', message: 'Erreur chargement opérateurs: ' + (err.message || 'inconnue') });
        setLoadingUsers(false);
      });
  }, []);

  const handleSubmit = async () => {
    setFeedback(null);

    // Client validation
    if (!userId) {
      setFeedback({ type: 'error', message: 'Veuillez sélectionner un opérateur.' });
      return;
    }
    const hoursNum = parseHHMM(hours);
    if (isNaN(hoursNum) || hoursNum <= 0) {
      setFeedback({ type: 'error', message: 'Les heures doivent être positives (ex: 1:30). Pour consommer des heures, utilisez un REC (Congés > encodage direct ou demande).' });
      return;
    }
    if (!date) {
      setFeedback({ type: 'error', message: 'Veuillez sélectionner une date.' });
      return;
    }

    setSubmitting(true);
    try {
      const result = await overtimeApi.addEntry({
        userId,
        date,
        hours: hoursNum,
        note: note.trim() || null,
      });

      const user = users.find(u => u.id === userId);
      const userName = user?.name || userId;
      const hoursStr = formatBalanceHours(hoursNum);

      if (result.warning === 'APPROACHING_CAP') {
        setFeedback({
          type: 'warning',
          message: `Entrée ajoutée pour ${userName}: ${hoursStr}. ` +
                   `⚠ Avertissement: solde mensuel projeté = ${formatBalanceHours(result.projectedSum)} ` +
                   `(seuil: ${result.warningThreshold}h, plafond: ${result.capHoursMonth}h).`,
        });
      } else {
        setFeedback({
          type: 'success',
          message: `Entrée ajoutée: ${hoursStr} pour ${userName}. Solde mensuel projeté: ${formatBalanceHours(result.projectedSum)}.`,
        });
      }

      // Clear form (keep userId for repeat ajout if same operator)
      setHours('');
      setNote('');
    } catch (err) {
      const apiErr = err.response?.data;
      let msg = apiErr?.message || err.message || 'Erreur inconnue';
      if (apiErr?.error === 'CAP_EXCEEDED') {
        msg = `🚫 ${msg}`;
      } else if (apiErr?.error === 'PERIOD_LOCKED') {
        msg = `🔒 Période verrouillée. ${msg}`;
      }
      setFeedback({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingUsers) {
    return <div style={{ padding: 24, color: '#64748b' }}>Chargement opérateurs…</div>;
  }

  return (
    <div style={{
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      padding: 24,
      maxWidth: 720,
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 16,
        marginBottom: 16,
      }}>
        <Field label="OPÉRATEUR">
          <select
            value={userId}
            onChange={e => setUserId(e.target.value)}
            style={selectStyle}
            disabled={submitting}
          >
            <option value="">— Sélectionner —</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.username})
              </option>
            ))}
          </select>
        </Field>

        <Field label="HEURES (hh:mm) — positif uniquement">
          <input
            type="text"
            inputMode="text"
            value={hours}
            onChange={e => setHours(e.target.value)}
            placeholder="Ex: 1:30"
            style={selectStyle}
            disabled={submitting}
          />
        </Field>

        <Field label="DATE">
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            style={selectStyle}
            disabled={submitting}
          />
        </Field>

        <Field label="NOTE">
          <input
            type="text"
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Optionnel"
            style={selectStyle}
            disabled={submitting}
            maxLength={500}
          />
        </Field>
      </div>

      {/* Feedback area */}
      {feedback && (
        <div style={{
          padding: 12,
          marginBottom: 16,
          borderRadius: 6,
          fontSize: 13,
          background:
            feedback.type === 'success' ? '#DCFCE7' :
            feedback.type === 'warning' ? '#FEF3C7' :
            '#FEE2E2',
          color:
            feedback.type === 'success' ? '#166534' :
            feedback.type === 'warning' ? '#78350F' :
            '#991B1B',
          border: '1px solid ' + (
            feedback.type === 'success' ? '#86EFAC' :
            feedback.type === 'warning' ? '#F59E0B' :
            '#FCA5A5'
          ),
        }}>
          {feedback.message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            padding: '10px 24px',
            background: submitting ? '#94a3b8' : '#15803d',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            cursor: submitting ? 'wait' : 'pointer',
          }}
        >
          {submitting ? 'En cours…' : 'Ajouter'}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Sub-component: SoldesTab (Day 6 / OT_DEC_V1)
// =====================================================================
// Context-aware:
//   - User with overtime.decide.all (Luca/Florent/Silviu) -> sees ALL pending decisions, can admin-force
//   - User without (operator) -> sees ONLY own pending decisions, can respond
// =====================================================================
// =====================================================================
// Sub-component: SoldesTab (Day 6 / OT_DEC_V1) — with toggle + StatusBadge
// =====================================================================
function SoldesTab({ onCount }) {
  const { can } = useAuth();
  const isAdminView = can('overtime.decide.all');

  const [loading, setLoading] = useState(true);
  const [decisions, setDecisions] = useState([]);
  const [error, setError] = useState(null);
  const [respondTarget, setRespondTarget] = useState(null);
  const [adminForceTarget, setAdminForceTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [showAll, setShowAll] = useState(false);

  async function loadDecisions() {
    setLoading(true);
    setError(null);
    try {
      const statusParam = showAll ? 'ALL' : 'PENDING';
      const list = isAdminView
        ? await overtimeApi.adminAllPendingDecisions(statusParam)
        : await overtimeApi.myPendingDecisions(statusParam);
      setDecisions(list);
      // Le parent affiche le compteur sur l'onglet : il ne compte QUE l'attente,
      // et il doit tenir même quand on affiche aussi les décisions réglées — cocher
      // une case d'AFFICHAGE ne règle aucune décision. Renvoyer null là faisait
      // disparaître le badge, c'est-à-dire exactement ce qu'on vient de rendre visible.
      if (onCount) {
        onCount(statusParam === 'PENDING'
          ? list.length
          : list.filter((d) => d.status === 'PENDING').length);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadDecisions(); }, [isAdminView, showAll]);

  async function handleRespondSave(decisionId, body) {
    await overtimeApi.respondDecision(decisionId, body);
    await loadDecisions();
  }
  async function handleAdminForceSave(decisionId, body) {
    await overtimeApi.adminForceDecision(decisionId, body);
    await loadDecisions();
  }

  const ToggleControl = (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
      <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
      Afficher aussi les décisions réglées
    </label>
  );

  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>Chargement...</div>;
  }
  if (error) {
    return (
      <div style={{ padding: 24, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b' }}>
        <strong>Erreur :</strong> {error}
        <button onClick={loadDecisions} style={{ marginLeft: 12, padding: '4px 10px', cursor: 'pointer' }}>Reessayer</button>
      </div>
    );
  }
  if (decisions.length === 0) {
    return (
      <div>
        <div style={{ marginBottom: 12 }}>{ToggleControl}</div>
        <div style={{ padding: 32, background: '#F0FDF4', border: '1px dashed #86EFAC', borderRadius: 8, textAlign: 'center', color: '#16a34a' }}>
          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
            {showAll ? 'Aucune decision' : 'Aucune decision en attente'}
          </div>
          <div style={{ fontSize: 13, color: '#15803d' }}>
            {isAdminView
              ? 'Tous les operateurs ont repondu, ou aucune decision n\u2019a ete declenchee.'
              : 'Vous n\u2019avez aucune decision. Si vous avez des heures supplementaires, attendez que la Finance declenche les decisions pour le mois.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, color: '#475569' }}>
        <div>
          {isAdminView
            ? <><strong>{decisions.length}</strong> décision(s) {showAll ? 'au total' : 'en attente'}</>
            : <><strong>{decisions.length}</strong> {showAll ? 'décision(s)' : 'décision(s) qui attendent votre réponse'}</>}
        </div>
        {ToggleControl}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ background: '#F1F5F9', borderBottom: '1px solid #CBD5E1' }}>
            {isAdminView && <th style={{ padding: 10, textAlign: 'left' }}>Operateur</th>}
            <th style={{ padding: 10, textAlign: 'left' }}>Mois</th>
            <th style={{ padding: 10, textAlign: 'right' }}>Solde</th>
            <th style={{ padding: 10, textAlign: 'center' }}>Entrees</th>
            <th style={{ padding: 10, textAlign: 'center' }}>Statut</th>
            <th style={{ padding: 10, textAlign: 'right' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {decisions.map((d) => (
            <tr key={d.id} style={{ borderBottom: '1px solid #E2E8F0' }}>
              {isAdminView && <td style={{ padding: 10 }}>{d.userName || d.userId}</td>}
              <td style={{ padding: 10 }}>{formatMonthFR(d.month)}</td>
              <td style={{ padding: 10, textAlign: 'right', fontWeight: 600, color: d.balanceAtDecision > 0 ? '#16a34a' : (d.balanceAtDecision < 0 ? '#dc2626' : '#64748b') }}>
                {d.balanceAtDecision > 0 ? '+' : ''}{d.balanceAtDecision}h
              </td>
              <td style={{ padding: 10, textAlign: 'center', color: '#64748b' }}>{d.entries.length}</td>
              <td style={{ padding: 10, textAlign: 'center' }}><StatusBadge status={d.status} /></td>
              <td style={{ padding: 10, textAlign: 'right' }}>
                {d.status === 'PENDING' ? (
                  isAdminView ? (
                    <button onClick={() => setAdminForceTarget(d)} style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                      Décider à sa place
                    </button>
                  ) : (
                    <button onClick={() => setRespondTarget(d)} style={{ padding: '6px 12px', background: '#16a34a', color: 'white', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                      Repondre
                    </button>
                  )
                ) : (
                  <button onClick={() => setViewTarget(d)} style={{ padding: '6px 12px', background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                    Voir details
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {respondTarget && (
        <DecisionRespondModal
          decision={respondTarget}
          isAdminMode={false}
          onClose={() => setRespondTarget(null)}
          onSave={(body) => handleRespondSave(respondTarget.id, body)}
        />
      )}
      {adminForceTarget && (
        <DecisionRespondModal
          decision={adminForceTarget}
          isAdminMode={true}
          onClose={() => setAdminForceTarget(null)}
          onSave={(body) => handleAdminForceSave(adminForceTarget.id, body)}
        />
      )}
      {viewTarget && (
        <DecisionViewModal decision={viewTarget} isAdminView={isAdminView} onClose={() => setViewTarget(null)} />
      )}
    </div>
  );
}

// =====================================================================
// Sub-component: DecisionRespondModal
// =====================================================================
// Shared modal for both user respond and admin-force.
// Contextual fields:
//   - balance > 0 : payHours + carryHours (deduct hidden)
//   - balance < 0 : deductHours + carryHours (pay hidden)
//   - balance = 0 : info only (no inputs)
// =====================================================================
function DecisionRespondModal({ decision, isAdminMode, onClose, onSave }) {
  const balance = decision.balanceAtDecision;
  const absBalance = Math.abs(balance);

  // Default: tout payer (or tout déduire si négatif)
  const [payHours, setPayHours] = useState(balance > 0 ? balance : 0);
  const [deductHours, setDeductHours] = useState(balance < 0 ? absBalance : 0);
  const [carryHours, setCarryHours] = useState(0);
  const [reason, setReason] = useState('ABSENT');
  const [reasonDetail, setReasonDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Auto-balance: when user changes one input, derive the other so sum matches absBalance
  function updatePay(v) {
    const p = parseFloat(v) || 0;
    setPayHours(p);
    setCarryHours(Math.max(0, Math.round((balance - p) * 100) / 100));
  }
  function updateDeduct(v) {
    const d = parseFloat(v) || 0;
    setDeductHours(d);
    setCarryHours(Math.min(0, Math.round((balance + d) * 100) / 100));
  }
  function updateCarry(v) {
    const c = parseFloat(v) || 0;
    setCarryHours(c);
    if (balance > 0) setPayHours(Math.max(0, Math.round((balance - c) * 100) / 100));
    else if (balance < 0) setDeductHours(Math.max(0, Math.round((Math.abs(balance) - Math.abs(c)) * 100) / 100));
  }

  const sumOK = balance > 0
    ? Math.abs(payHours + carryHours - balance) < 0.01
    : balance < 0
      ? Math.abs(deductHours + Math.abs(carryHours) - absBalance) < 0.01
      : (payHours === 0 && deductHours === 0 && carryHours === 0);

  async function handleSubmit() {
    setError(null);
    if (!sumOK) {
      setError('La somme ne correspond pas au solde.');
      return;
    }
    if (isAdminMode && !reason) {
      setError('Motif obligatoire pour décision admin.');
      return;
    }
    setSubmitting(true);
    try {
      const body = { payHours, deductHours, carryHours };
      if (isAdminMode) {
        body.reason = reason;
        body.reasonDetail = reasonDetail.trim() || null;
      }
      await onSave(body);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 8, padding: 24, maxWidth: 560, width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>
            {isAdminMode ? `Décider pour ${decision.userName || decision.userId}` : 'Répondre à la décision'}
          </h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: 24, cursor: 'pointer' }} aria-label="Fermer">×</button>
        </div>

        <div style={{ background: '#F1F5F9', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
          <div style={{ marginBottom: 4 }}>Mois : <strong>{formatMonthFR(decision.month)}</strong></div>
          {decision.reportPrevious != null && Math.abs(decision.reportPrevious) >= 0.01 && (
            <div style={{ marginBottom: 4, color: '#475569' }}>
              Report des mois précédents : <strong style={{ color: decision.reportPrevious > 0 ? '#16a34a' : '#dc2626' }}>{decision.reportPrevious > 0 ? '+' : ''}{decision.reportPrevious}h</strong>
            </div>
          )}
          <div>Solde {decision.reportPrevious != null && Math.abs(decision.reportPrevious) >= 0.01 ? 'cumulé' : ''} : <strong style={{ color: balance > 0 ? '#16a34a' : (balance < 0 ? '#dc2626' : '#64748b') }}>{balance > 0 ? '+' : ''}{balance}h</strong></div>
        </div>

        <div style={{ background: '#FFFBEB', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 12, borderLeft: '3px solid #fbbf24' }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Détail des entrées :</div>
          {decision.entries.map((e) => (
            <div key={e.id}>• {e.date} : {e.hours > 0 ? '+' : ''}{e.hours}h {e.type === 'MANUAL' ? '(Manuel)' : `(${e.type})`}{e.note ? ` — ${e.note}` : ''}</div>
          ))}
        </div>

        {balance !== 0 ? (
          <div style={{ marginBottom: 16 }}>
            {balance > 0 && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>💰 À payer (h)</label>
                  <input type="number" step="0.5" min="0" max={balance} value={payHours} onChange={(e) => updatePay(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4 }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>↪ À reporter (h)</label>
                  <input type="number" step="0.5" min="0" max={balance} value={carryHours} onChange={(e) => updateCarry(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4 }} />
                </div>
              </>
            )}
            {balance < 0 && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>➖ À déduire du salaire (h)</label>
                  <input type="number" step="0.5" min="0" max={absBalance} value={deductHours} onChange={(e) => updateDeduct(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4 }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>↪ À reporter en négatif (h)</label>
                  <input type="number" step="0.5" min={-absBalance} max="0" value={carryHours} onChange={(e) => updateCarry(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4 }} />
                </div>
              </>
            )}
            <div style={{ fontSize: 12, color: sumOK ? '#16a34a' : '#dc2626' }}>
              {sumOK ? '✓ Somme correcte' : `⚠ La somme doit être égale à ${balance > 0 ? '+' : ''}${balance}h`}
            </div>
          </div>
        ) : (
          <div style={{ padding: 12, background: '#f0fdf4', borderRadius: 6, marginBottom: 16, fontSize: 13, color: '#15803d' }}>
            Solde nul — rien à décider, validation enregistre simplement la décision.
          </div>
        )}

        {isAdminMode && (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Motif obligatoire</label>
              <select value={reason} onChange={(e) => setReason(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4 }}>
                <option value="ABSENT">Opérateur absent (maladie, congé)</option>
                <option value="REFUS">Refus de répondre</option>
                <option value="HORS_DELAI">Hors délai</option>
                <option value="AUTRE">Autre raison</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Détail (facultatif)</label>
              <textarea value={reasonDetail} onChange={(e) => setReasonDetail(e.target.value)} rows={2} placeholder="Ex : Concediu medical jusqu'au 30 juin" style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 4, fontFamily: 'inherit', resize: 'vertical' }} maxLength={500} />
            </div>
          </>
        )}

        {error && (
          <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, color: '#991b1b', marginBottom: 16, fontSize: 13 }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={submitting} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}>Annuler</button>
          <button onClick={handleSubmit} disabled={submitting || !sumOK} style={{ padding: '8px 16px', background: isAdminMode ? '#dc2626' : '#16a34a', color: 'white', border: 0, borderRadius: 6, cursor: submitting || !sumOK ? 'not-allowed' : 'pointer', opacity: submitting || !sumOK ? 0.6 : 1 }}>
            {submitting ? 'Enregistrement…' : (isAdminMode ? '⚠ Valider décision admin' : '✓ Confirmer ma décision')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper local (must match what Overtime.jsx already exports — if absent, define here)
function formatMonthFR(month) {
  if (!month || !month.match(/^\d{4}-\d{2}$/)) return month || '';
  const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

// =====================================================================
// Helpers UI
// =====================================================================
const selectStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #CBD5E1',
  borderRadius: 6,
  fontSize: 14,
  fontFamily: 'inherit',
  background: 'white',
};

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: 11,
        fontWeight: 500,
        color: '#64748B',
        marginBottom: 4,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}

// =====================================================================
// Sub-component: StatusBadge
// =====================================================================
function StatusBadge({ status }) {
  const STYLES = {
    PENDING:      { bg: '#fef3c7', fg: '#92400e', label: 'En attente' },
    CONFIRMED:    { bg: '#d1fae5', fg: '#065f46', label: 'Confirme' },
    ADMIN_FORCED: { bg: '#fee2e2', fg: '#991b1b', label: 'Force admin' },
  };
  const s = STYLES[status] || { bg: '#e5e7eb', fg: '#374151', label: status };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 8px', borderRadius: 12,
      background: s.bg, color: s.fg, fontSize: 11, fontWeight: 500,
    }}>{s.label}</span>
  );
}

// =====================================================================
// Sub-component: DecisionViewModal (read-only)
// =====================================================================
function DecisionViewModal({ decision, isAdminView, onClose }) {
  const d = decision;
  const balance = d.balanceAtDecision;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 8, padding: 24, maxWidth: 560, width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Details decision — {formatMonthFR(d.month)}</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: 24, cursor: 'pointer' }} aria-label="Fermer">×</button>
        </div>

        <div style={{ background: '#F1F5F9', padding: 12, borderRadius: 6, marginBottom: 14, fontSize: 13 }}>
          {isAdminView && d.userName && <div style={{ marginBottom: 4 }}>Operateur : <strong>{d.userName}</strong></div>}
          <div style={{ marginBottom: 4 }}>Statut : <StatusBadge status={d.status} /></div>
          <div style={{ marginBottom: 4 }}>Solde : <strong style={{ color: balance > 0 ? '#16a34a' : (balance < 0 ? '#dc2626' : '#64748b') }}>{balance > 0 ? '+' : ''}{balance}h</strong></div>
          {d.decidedAt && <div>Decide le : {new Date(d.decidedAt).toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} ({d.decidedBy === 'ADMIN' ? 'admin' : 'operateur'})</div>}
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 500, marginBottom: 6, fontSize: 13 }}>Repartition :</div>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td style={{ padding: 6 }}>A payer</td><td style={{ padding: 6, textAlign: 'right', fontWeight: 500 }}>{d.payHours != null ? d.payHours : 0}h</td></tr>
              <tr><td style={{ padding: 6 }}>A deduire</td><td style={{ padding: 6, textAlign: 'right', fontWeight: 500 }}>{d.deductHours != null ? d.deductHours : 0}h</td></tr>
              <tr><td style={{ padding: 6 }}>A reporter</td><td style={{ padding: 6, textAlign: 'right', fontWeight: 500 }}>{d.carryHours != null ? d.carryHours : 0}h</td></tr>
            </tbody>
          </table>
        </div>

        {d.reason && (
          <div style={{ background: '#fef2f2', padding: 10, borderRadius: 6, marginBottom: 14, fontSize: 13, borderLeft: '3px solid #fca5a5' }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>Motif (decision admin) : {d.reason}</div>
            {d.reasonDetail && <div style={{ color: '#64748b' }}>{d.reasonDetail}</div>}
          </div>
        )}

        <div style={{ background: '#FFFBEB', padding: 12, borderRadius: 6, marginBottom: 14, fontSize: 12, borderLeft: '3px solid #fbbf24' }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Entrees associees :</div>
          {d.entries.map((e) => (
            <div key={e.id}>{e.date} : {e.hours > 0 ? '+' : ''}{e.hours}h {e.type === 'MANUAL' ? '(Manuel)' : '(' + e.type + ')'}{e.note ? ' — ' + e.note : ''}</div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Sub-component: SoldesOperateursTab (Day 9)
// =====================================================================
// Tabel cu toți operatorii + solde cumulé + sold mois en cours + plafond + statut.
// Toolbar: search nume, filtre statut, tri, toggle inclure inactifs, période snapshot, export.
// Buton "⚙ Plafonds par groupe" deschide modal de editare per grup (cap + warning).
// =====================================================================
function SoldesOperateursTab({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all|ok|approche|depasse|negatif
  const [sortBy, setSortBy] = useState('name'); // name|balance|anomalies|lastEntry
  const [includeInactive, setIncludeInactive] = useState(false);
  const [periodSnapshot, setPeriodSnapshot] = useState('month'); // month|cumul

  // Modal Plafond
  const [showPlafondModal, setShowPlafondModal] = useState(false);
  // Drill-down: user entries modal
  const [drillUser, setDrillUser] = useState(null); // balance row sau null

  async function loadBalances() {
    setLoading(true);
    setError(null);
    try {
      const list = await overtimeApi.adminBalances(includeInactive);
      setRows(list);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadBalances(); }, [includeInactive]);

  const filteredSortedRows = useMemo(() => {
    let list = [...rows];

    // Filter: search nume
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) =>
        r.userName?.toLowerCase().includes(q) ||
        r.username?.toLowerCase().includes(q) ||
        r.groupName?.toLowerCase().includes(q)
      );
    }

    // Filter: statut
    if (statusFilter !== 'all') {
      list = list.filter((r) => {
        if (statusFilter === 'depasse') return r.isOverCap;
        if (statusFilter === 'approche') return r.isApproachingCap && !r.isOverCap;
        if (statusFilter === 'negatif') return r.balanceHours < 0;
        if (statusFilter === 'ok') return !r.isApproachingCap && !r.isOverCap && r.balanceHours >= 0;
        return true;
      });
    }

    // Sort
    list.sort((a, b) => {
      if (sortBy === 'name') return comparerPersonnes(a, b);
      if (sortBy === 'balance') return (b.balanceHours || 0) - (a.balanceHours || 0);
      if (sortBy === 'lastEntry') return (b.lastEntryDate || '').localeCompare(a.lastEntryDate || '');
      if (sortBy === 'anomalies') {
        const sa = (a.isOverCap ? 2 : 0) + (a.isApproachingCap ? 1 : 0);
        const sb = (b.isOverCap ? 2 : 0) + (b.isApproachingCap ? 1 : 0);
        if (sa !== sb) return sb - sa;
        return comparerPersonnes(a, b);
      }
      return 0;
    });

    return list;
  }, [rows, search, statusFilter, sortBy]);

  function exportCSV() {
    const headers = ['Opérateur', 'Username', 'Rôle', 'Groupe', 'Solde cumulé', 'Mois en cours', 'Plafond', 'Seuil avertissement', 'Statut', 'Dernière entrée', 'Actif'];
    const lines = [headers.join(',')];
    filteredSortedRows.forEach((r) => {
      const status = r.isOverCap ? 'Dépassé' : (r.isApproachingCap ? 'Approche' : (r.balanceHours < 0 ? 'Négatif' : 'OK'));
      const fields = [
        r.userName, r.username, r.role, r.groupName || '',
        formatBalanceHours(r.balanceHours),
        formatBalanceHours(r.currentMonthSum),
        r.capHoursMonth + 'h',
        r.warningThreshold + 'h',
        status,
        r.lastEntryDate || '',
        r.isActive ? 'Oui' : 'Non',
      ].map(celluleCsv);   // formule (= + - @) neutralisée : lib/csv.js
      lines.push(fields.join(','));
    });
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `soldes-operateurs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>Chargement...</div>;
  }
  if (error) {
    return (
      <div style={{ padding: 24, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b' }}>
        <strong>Erreur :</strong> {error}
        <button onClick={loadBalances} style={{ marginLeft: 12, padding: '4px 10px', cursor: 'pointer' }}>Réessayer</button>
      </div>
    );
  }

  const counts = {
    total: rows.length,
    ok: rows.filter((r) => !r.isApproachingCap && !r.isOverCap && r.balanceHours >= 0).length,
    approche: rows.filter((r) => r.isApproachingCap && !r.isOverCap).length,
    depasse: rows.filter((r) => r.isOverCap).length,
    negatif: rows.filter((r) => r.balanceHours < 0).length,
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
        <input
          type="text"
          placeholder="🔍 Rechercher (nom, username, groupe)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minWidth: 260, flex: 1, maxWidth: 360 }}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={tbSelectStyle}>
          <option value="all">Tous les statuts ({counts.total})</option>
          <option value="ok">OK ({counts.ok})</option>
          <option value="approche">⚠ Approche ({counts.approche})</option>
          <option value="depasse">⚠⚠ Dépassé ({counts.depasse})</option>
          <option value="negatif">Négatif ({counts.negatif})</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={tbSelectStyle}>
          <option value="name">Tri : Nom</option>
          <option value="balance">Tri : Solde décroissant</option>
          <option value="anomalies">Tri : Anomalies en tête</option>
          <option value="lastEntry">Tri : Dernière entrée</option>
        </select>
        <select value={periodSnapshot} onChange={(e) => setPeriodSnapshot(e.target.value)} style={tbSelectStyle} title="Quel solde mettre en avant dans la colonne principale">
          <option value="month">Affichage : Solde mois en cours</option>
          <option value="cumul">Affichage : Solde cumulé</option>
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          Inclure inactifs
        </label>
        <div style={{ flex: '1 0 auto' }} />
        <button onClick={exportCSV} style={tbBtnStyle} title="Export CSV avec filtres appliqués">📥 CSV</button>
        {canEdit && (
          <button onClick={() => setShowPlafondModal(true)} style={{ ...tbBtnStyle, background: '#1e40af', color: 'white', border: '1px solid #1e3a8a' }}>
            ⚙ Plafonds par groupe
          </button>
        )}
      </div>

      <div style={{ marginBottom: 8, fontSize: 13, color: '#475569' }}>
        <strong>{filteredSortedRows.length}</strong> opérateur(s) affiché(s) sur <strong>{counts.total}</strong>.
      </div>

      {filteredSortedRows.length === 0 ? (
        <div style={{ padding: 32, background: '#F0FDF4', border: '1px dashed #86EFAC', borderRadius: 8, textAlign: 'center', color: '#16a34a' }}>
          Aucun opérateur ne correspond aux filtres.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F1F5F9', borderBottom: '1px solid #CBD5E1' }}>
                <th style={{ padding: 10, textAlign: 'left' }}>Opérateur</th>
                <th style={{ padding: 10, textAlign: 'left' }}>Groupe</th>
                <th style={{ padding: 10, textAlign: 'right' }}>{periodSnapshot === 'cumul' ? 'Solde cumulé' : 'Mois en cours'}</th>
                <th style={{ padding: 10, textAlign: 'right' }}>{periodSnapshot === 'cumul' ? 'Mois en cours' : 'Solde cumulé'}</th>
                <th style={{ padding: 10, textAlign: 'center' }}>Plafond</th>
                <th style={{ padding: 10, textAlign: 'center' }}>Statut</th>
                <th style={{ padding: 10, textAlign: 'center' }}>Dernière entrée</th>
              </tr>
            </thead>
            <tbody>
              {filteredSortedRows.map((r) => {
                const primaryVal = periodSnapshot === 'cumul' ? r.balanceHours : r.currentMonthSum;
                const secondaryVal = periodSnapshot === 'cumul' ? r.currentMonthSum : r.balanceHours;
                const statusLabel = r.isOverCap ? '⚠⚠ Dépassé'
                                  : r.isApproachingCap ? '⚠ Approche'
                                  : r.balanceHours < 0 ? 'Négatif'
                                  : '✓ OK';
                const statusPillStyle = r.isOverCap ? pillOverStyle
                                       : r.isApproachingCap ? pillWarnStyle
                                       : r.balanceHours < 0 ? pillNegStyle
                                       : pillOkStyle;
                return (
                  <tr
                    key={r.userId}
                    onClick={() => setDrillUser(r)}
                    style={{ borderBottom: '1px solid #E2E8F0', opacity: r.isActive ? 1 : 0.55, cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    title="Cliquer pour voir toutes les encodages"
                  >
                    <td style={{ padding: 10 }}>
                      <div>{r.userName}{!r.isActive && <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8' }}>(inactif)</span>}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{r.username} · {r.role}</div>
                    </td>
                    <td style={{ padding: 10, color: '#475569' }}>{r.groupName || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                    <td style={{ padding: 10, textAlign: 'right' }}><span style={pillForValue(primaryVal)}>{formatBalanceHours(primaryVal)}</span></td>
                    <td style={{ padding: 10, textAlign: 'right', color: '#64748b' }}>{formatBalanceHours(secondaryVal)}</td>
                    <td style={{ padding: 10, textAlign: 'center', color: '#64748b', fontSize: 12 }}>{r.capHoursMonth}h <span style={{ color: '#cbd5e1' }}>/</span> {r.warningThreshold}h</td>
                    <td style={{ padding: 10, textAlign: 'center' }}><span style={statusPillStyle}>{statusLabel}</span></td>
                    <td style={{ padding: 10, textAlign: 'center', color: '#64748b', fontSize: 12 }}>{r.lastEntryDate || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showPlafondModal && (
        <PlafondModal
          onClose={() => setShowPlafondModal(false)}
          onSaved={() => { loadBalances(); }}
        />
      )}

      {drillUser && (
        <UserEntriesModal
          row={drillUser}
          onClose={() => setDrillUser(null)}
        />
      )}
    </div>
  );
}

// Toolbar styles + status pills
const tbSelectStyle = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: 'white' };
const tbBtnStyle = { padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 13 };
const pillBaseStyle = { display: 'inline-block', padding: '3px 8px', borderRadius: 6, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' };
const pillOkStyle   = { ...pillBaseStyle, background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1' };
const pillPosStyle  = { ...pillBaseStyle, background: '#FAEEDA', color: '#633806', border: '1px solid #E8D4B0' };
const pillNegStyle  = { ...pillBaseStyle, background: '#FCEBEB', color: '#791F1F', border: '1px solid #E5C2C2' };
const pillWarnStyle = { ...pillBaseStyle, background: '#FEF3C7', color: '#78350F', border: '1px solid #F59E0B' };
const pillOverStyle = { ...pillBaseStyle, background: '#FEE2E2', color: '#7F1D1D', border: '1px solid #DC2626' };
function pillForValue(v) {
  if (v === null || v === undefined || v === 0) return pillOkStyle;
  return v > 0 ? pillPosStyle : pillNegStyle;
}

// =====================================================================
// Sub-component: PlafondModal — edit OvertimeGroupSetting per group
// =====================================================================
function PlafondModal({ onClose, onSaved }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edits, setEdits] = useState({}); // { [groupId]: { cap, warn } as strings (hh:mm friendly is decimal h here) }
  const [savingId, setSavingId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const list = await overtimeApi.adminGroupSettings();
      setGroups(list);
      const initialEdits = {};
      list.forEach((g) => {
        initialEdits[g.groupId] = {
          cap: String(g.capHoursMonth),
          warn: String(g.warningThreshold),
        };
      });
      setEdits(initialEdits);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function updateField(groupId, field, value) {
    setEdits((prev) => ({
      ...prev,
      [groupId]: { ...prev[groupId], [field]: value },
    }));
  }

  function hasChanged(g) {
    const e = edits[g.groupId];
    if (!e) return false;
    return Number(e.cap) !== g.capHoursMonth || Number(e.warn) !== g.warningThreshold;
  }

  async function saveGroup(g) {
    const e = edits[g.groupId];
    const cap = Number(e.cap);
    const warn = Number(e.warn);
    if (!Number.isFinite(cap) || cap <= 0) {
      setFeedback({ type: 'error', message: `${g.groupName} : plafond doit être un nombre > 0` });
      return;
    }
    if (!Number.isFinite(warn) || warn <= 0) {
      setFeedback({ type: 'error', message: `${g.groupName} : seuil doit être un nombre > 0` });
      return;
    }
    if (warn >= cap) {
      setFeedback({ type: 'error', message: `${g.groupName} : seuil (${warn}h) doit être < plafond (${cap}h)` });
      return;
    }

    setSavingId(g.groupId);
    setFeedback(null);
    try {
      await overtimeApi.updateGroupSetting(g.groupId, { capHoursMonth: cap, warningThreshold: warn });
      setFeedback({ type: 'success', message: `${g.groupName} : enregistré (plafond ${cap}h, seuil ${warn}h).` });
      await load();
      if (onSaved) onSaved();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Erreur inconnue';
      setFeedback({ type: 'error', message: `${g.groupName} : ${msg}` });
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
    }}>
      <div style={{ background: 'white', borderRadius: 8, padding: 20, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>⚙ Plafonds heures supplémentaires par groupe</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>
        <p style={{ marginTop: 0, marginBottom: 16, color: '#64748b', fontSize: 13 }}>
          Modifie le plafond mensuel et le seuil d'avertissement pour chaque groupe. Effet immédiat sur toutes les nouvelles validations.
          Le seuil doit être strictement inférieur au plafond.
        </p>

        {feedback && (
          <div style={{
            padding: 10, marginBottom: 14, borderRadius: 6, fontSize: 13,
            background: feedback.type === 'success' ? '#dcfce7' : '#fee2e2',
            color: feedback.type === 'success' ? '#166534' : '#991b1b',
            border: `1px solid ${feedback.type === 'success' ? '#86efac' : '#fca5a5'}`,
          }}>{feedback.message}</div>
        )}

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>Chargement...</div>
        ) : error ? (
          <div style={{ padding: 16, color: '#991b1b' }}>{error}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F1F5F9' }}>
                <th style={{ padding: 8, textAlign: 'left' }}>Groupe</th>
                <th style={{ padding: 8, textAlign: 'center' }}>Users</th>
                <th style={{ padding: 8, textAlign: 'center' }}>Plafond (h/mois)</th>
                <th style={{ padding: 8, textAlign: 'center' }}>Seuil avertis. (h)</th>
                <th style={{ padding: 8, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const e = edits[g.groupId] || { cap: '', warn: '' };
                const changed = hasChanged(g);
                return (
                  <tr key={g.groupId} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: 8 }}>
                      <div style={{ fontWeight: 600 }}>{g.groupName}</div>
                      {g.isAdmin && <div style={{ fontSize: 11, color: '#7c3aed' }}>Groupe admin</div>}
                    </td>
                    <td style={{ padding: 8, textAlign: 'center', color: '#64748b' }}>{g.userCount}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      <input
                        type="number"
                        step="0.5"
                        min="0.5"
                        value={e.cap}
                        onChange={(ev) => updateField(g.groupId, 'cap', ev.target.value)}
                        style={{ width: 80, padding: '4px 6px', textAlign: 'right', border: changed ? '1px solid #f59e0b' : '1px solid #cbd5e1', borderRadius: 4 }}
                      />
                    </td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      <input
                        type="number"
                        step="0.5"
                        min="0.5"
                        value={e.warn}
                        onChange={(ev) => updateField(g.groupId, 'warn', ev.target.value)}
                        style={{ width: 80, padding: '4px 6px', textAlign: 'right', border: changed ? '1px solid #f59e0b' : '1px solid #cbd5e1', borderRadius: 4 }}
                      />
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      <button
                        onClick={() => saveGroup(g)}
                        disabled={!changed || savingId === g.groupId}
                        style={{
                          padding: '5px 12px',
                          background: changed ? '#16a34a' : '#f1f5f9',
                          color: changed ? 'white' : '#94a3b8',
                          border: 0, borderRadius: 6,
                          cursor: changed ? 'pointer' : 'not-allowed',
                          fontSize: 12,
                        }}
                      >
                        {savingId === g.groupId ? '...' : 'Enregistrer'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Sub-component: UserEntriesModal — drill-down toate encodările unui user
// =====================================================================
function UserEntriesModal({ row, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'month' | 'previous'

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filter === 'month') {
        params.period = new Date().toISOString().slice(0, 7);
      } else if (filter === 'previous') {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        params.period = d.toISOString().slice(0, 7);
      }
      const list = await overtimeApi.adminListUserEntries(row.userId, params);
      setEntries(list);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [filter, row.userId]);

  // Day 11 — edit/delete entrée MANUAL non-réglée
  async function handleEdit(e) {
    if (e.decisionId && !window.confirm('⚠️ Cette entrée fait partie d\'une décision mensuelle déjà prise. La modifier ne recalcule PAS la décision. Continuer ?')) return;
    const cur = formatBalanceHours(Math.abs(e.hours)).replace('+', '');
    const input = window.prompt(`Modifier les heures (format hh:mm, positif) pour le ${e.date} :`, cur);
    if (input === null) return;
    const h = parseHHMM(input);
    if (isNaN(h) || h <= 0) { window.alert('Valeur invalide. Format hh:mm positif (ex: 1:30).'); return; }
    try { await overtimeApi.updateEntry(e.id, { hours: h }); await load(); }
    catch (err) { window.alert(err?.response?.data?.message || err.message); }
  }
  async function handleDelete(e) {
    const warn = e.decisionId
      ? '⚠️ Cette entrée fait partie d\'une décision mensuelle déjà prise. La supprimer ne recalcule PAS la décision.\n\n'
      : '';
    if (!window.confirm(`${warn}Supprimer cette entrée du ${e.date} (${formatBalanceHours(e.hours)}) ? Action irréversible.`)) return;
    try { await overtimeApi.deleteEntry(e.id); await load(); }
    catch (err) { window.alert(err?.response?.data?.message || err.message); }
  }

  const totalShown = entries.reduce((s, e) => s + (e.hours || 0), 0);

  const TYPE_LABELS = {
    MANUAL:          'Manuel',
    AUTO_FROM_TS:    'Auto (TS)',
    REC_CONSUME:     'REC consommé',
    PAID_OUT:        'Payé',
    DEDUCTED_SALARY: 'Déduit salaire',
    CARRY_FORWARD:   'Report',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
    }}>
      <div style={{ background: 'white', borderRadius: 8, padding: 20, maxWidth: 820, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>{row.userName}</h2>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              {row.username} · {row.role} · {row.groupName || '—'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        {/* Summary banner */}
        <div style={{ display: 'flex', gap: 16, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Solde cumulé</div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>
              <span style={pillForValue(row.balanceHours)}>{formatBalanceHours(row.balanceHours)}</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Mois en cours</div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>
              <span style={pillForValue(row.currentMonthSum)}>{formatBalanceHours(row.currentMonthSum)}</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Plafond / Seuil</div>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#475569' }}>{row.capHoursMonth}h <span style={{ color: '#cbd5e1' }}>/</span> {row.warningThreshold}h</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Dernière entrée</div>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#475569' }}>{row.lastEntryDate || '—'}</div>
          </div>
        </div>

        {/* Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, fontSize: 13 }}>
          <span>Période :</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} style={tbSelectStyle}>
            <option value="all">Toutes les encodages</option>
            <option value="month">Mois en cours</option>
            <option value="previous">Mois précédent</option>
          </select>
          <div style={{ flex: 1 }} />
          <span style={{ color: '#64748b' }}>
            <strong>{entries.length}</strong> entrée(s) · Total <strong>{formatBalanceHours(totalShown)}</strong>
          </span>
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>Chargement...</div>
        ) : error ? (
          <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#991b1b' }}>{error}</div>
        ) : entries.length === 0 ? (
          <div style={{ padding: 24, background: '#F0FDF4', border: '1px dashed #86EFAC', borderRadius: 8, textAlign: 'center', color: '#16a34a' }}>
            Aucune encodage pour la période sélectionnée.
          </div>
        ) : (
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F1F5F9' }}>
                  <th style={{ padding: 8, textAlign: 'left' }}>Date</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Heures</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Type</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Note</th>
                  <th style={{ padding: 8, textAlign: 'center' }}>Réglé ?</th>
                  <th style={{ padding: 8, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  // Day 11 — éditable: toute entrée MANUAL (même réglée, avec avertissement)
                  const editable = e.type === 'MANUAL';
                  return (
                  <tr key={e.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: 8 }}>{e.date}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      <span style={pillForValue(e.hours)}>{formatBalanceHours(e.hours)}</span>
                    </td>
                    <td style={{ padding: 8, color: '#475569' }}>{TYPE_LABELS[e.type] || e.type}</td>
                    <td style={{ padding: 8, color: '#475569' }}>{e.note || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                    <td style={{ padding: 8, textAlign: 'center', fontSize: 11 }}>
                      {e.decisionId
                        ? <span style={{ color: '#16a34a' }}>✓ Réglé</span>
                        : <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                    <td style={{ padding: 8, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {editable ? (
                        <>
                          <button onClick={() => handleEdit(e)} title="Modifier" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, marginRight: 6 }}>✏️</button>
                          <button onClick={() => handleDelete(e)} title="Supprimer" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, color: '#dc2626' }}>🗑</button>
                        </>
                      ) : <span style={{ color: '#cbd5e1', fontSize: 11 }}>—</span>}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer' }}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
